/**
 * Where the repertoire scene puts a score's finger numbers.
 *
 * OSMD draws each finger number centred on its notehead, above the upper staff or
 * below the lower one, and stacks a chord's numbers in pitch order. The scene draws
 * them smaller than OSMD does, so it moves them afterwards. The rules:
 *
 * - A number keeps OSMD's centre, so it stays on its notehead.
 * - A chord's numbers stay one stack, in OSMD's order, packed to the smaller size.
 * - A number shrinks about the edge that faces the staff, so it keeps its distance.
 * - A stack stays outside the staves: above the upper staff's top line or below the
 *   lower staff's bottom line, never on the lines among the notes of another voice.
 * - A stack never sits on its note's stem or beam: when the stem points at it, the
 *   stack moves past the stem's tip (a beam joins the stems at their tips).
 * - Stacks close to the staff line up in one row (the row of the stack furthest out
 *   among them); a stack well away from the staff (a note on ledger lines) keeps its
 *   own height next to its note.
 * - A stack never covers another marking (a dynamic, a word): it moves on past it.
 *
 * Every number here is in the phrase's drawing coordinates (y grows downwards).
 */

export type FingerMarkSide = 'above' | 'below';

export type FingerMark = {
  side: FingerMarkSide;
  /** Horizontal centre of the number as OSMD drew it. */
  centerX: number;
  /** Bottom of the number as OSMD drew it. */
  bottom: number;
  /** Height of the number as OSMD drew it. */
  height: number;
  /** Width and height of the number at the size the scene draws it. */
  drawnWidth: number;
  drawnHeight: number;
};

export type Stem = {
  x: number;
  /** The end away from the notehead. */
  tipY: number;
  direction: 'up' | 'down';
};

export type FingerMarkLayoutOptions = {
  /** How much the drawn size is of OSMD's (stack spacing shrinks with it). */
  sizeRatio: number;
  /** Space kept between a stem's tip and the number. */
  stemGap: number;
  /** Stacks whose staff-side edges are this close to the row join it. */
  rowTolerance: number;
  /** The upper staff's top line and the lower staff's bottom line, when known. */
  staffSpan?: { top: number; bottom: number } | null;
};

/** Another marking's box (a dynamic, a word) that a finger number must not cover. */
export type Obstacle = { left: number; right: number; top: number; bottom: number };

export type PlacedFingerMark = { left: number; bottom: number };

/** Numbers drawn this close together horizontally belong to one stack (one chord). */
const SAME_STACK_X = 1.5;

/**
 * A stem this close to a number's side belongs to its note: a stem sits at the edge of
 * its notehead, about half a notehead from the number's centre.
 */
const STEM_SIDE_ROOM = 3;

type Stack = { side: FingerMarkSide; centerX: number; members: number[] };

/** The edge of a mark that faces the staff: its bottom above, its top below. */
const staffSideEdge = (mark: FingerMark): number =>
  mark.side === 'above' ? mark.bottom : mark.bottom - mark.height;

/** Further from the staff than `b` on this side. */
const isFurtherOut = (side: FingerMarkSide, a: number, b: number): boolean =>
  side === 'above' ? a < b : a > b;

const furthestOut = (side: FingerMarkSide, edges: number[]): number =>
  side === 'above' ? Math.min(...edges) : Math.max(...edges);

const nearestIn = (side: FingerMarkSide, edges: number[]): number =>
  side === 'above' ? Math.max(...edges) : Math.min(...edges);

const groupIntoStacks = (marks: FingerMark[]): Stack[] =>
  marks.reduce<Stack[]>((stacks, mark, index) => {
    const stack = stacks.find(
      (candidate) =>
        candidate.side === mark.side && Math.abs(candidate.centerX - mark.centerX) <= SAME_STACK_X,
    );
    if (!stack) return [...stacks, { side: mark.side, centerX: mark.centerX, members: [index] }];
    return stacks.map((candidate) =>
      candidate === stack ? { ...candidate, members: [...candidate.members, index] } : candidate,
    );
  }, []);

/** The stack's members nearest the staff first. */
const membersFromTheStaff = (stack: Stack, marks: FingerMark[]): number[] =>
  [...stack.members].sort((a, b) =>
    stack.side === 'above' ? marks[b].bottom - marks[a].bottom : marks[a].bottom - marks[b].bottom,
  );

/** The staff-side edge after moving clear of any stem that points into the stack. */
const clearOfStems = (
  stack: Stack,
  edge: number,
  halfWidth: number,
  stems: Stem[],
  stemGap: number,
): number =>
  stems
    .filter(
      (stem) =>
        stem.direction === (stack.side === 'above' ? 'up' : 'down') &&
        Math.abs(stem.x - stack.centerX) <= halfWidth + STEM_SIDE_ROOM,
    )
    .map((stem) => (stack.side === 'above' ? stem.tipY - stemGap : stem.tipY + stemGap))
    .reduce(
      (current, needed) => (isFurtherOut(stack.side, needed, current) ? needed : current),
      edge,
    );

/** The staff-side edge moved out of the staves, if it was inside them. */
const outsideTheStaves = (
  side: FingerMarkSide,
  edge: number,
  options: FingerMarkLayoutOptions,
): number => {
  if (!options.staffSpan) return edge;
  return side === 'above'
    ? Math.min(edge, options.staffSpan.top - options.stemGap)
    : Math.max(edge, options.staffSpan.bottom + options.stemGap);
};

/** The staff-side edge after moving past every marking the stack would cover. */
const clearOfObstacles = (
  stack: Stack,
  edge: number,
  halfWidth: number,
  extent: number,
  obstacles: Obstacle[],
  gap: number,
): number =>
  [...obstacles]
    .filter(
      (obstacle) =>
        obstacle.right > stack.centerX - halfWidth && obstacle.left < stack.centerX + halfWidth,
    )
    .sort((a, b) => (stack.side === 'above' ? b.bottom - a.bottom : a.top - b.top))
    .reduce((current, obstacle) => {
      const top = stack.side === 'above' ? current - extent : current;
      const bottom = stack.side === 'above' ? current : current + extent;
      const covers = obstacle.top < bottom && obstacle.bottom > top;
      if (!covers) return current;
      return stack.side === 'above' ? obstacle.top - gap : obstacle.bottom + gap;
    }, edge);

/**
 * Lay out a phrase's finger numbers. Returns, for each mark in order, the left edge
 * and bottom at which to draw it at its drawn size.
 */
export const layoutFingerMarks = (
  marks: FingerMark[],
  stems: Stem[],
  options: FingerMarkLayoutOptions,
  obstacles: Obstacle[] = [],
): PlacedFingerMark[] => {
  if (!Array.isArray(marks) || !Array.isArray(stems) || !Array.isArray(obstacles)) {
    throw new TypeError('layoutFingerMarks: marks, stems and obstacles must be arrays');
  }
  const stacks = groupIntoStacks(marks).map((stack) => {
    const ordered = membersFromTheStaff(stack, marks);
    const nearest = marks[ordered[0]];
    const halfWidth = Math.max(...ordered.map((index) => marks[index].drawnWidth)) / 2;
    const edge = outsideTheStaves(
      stack.side,
      clearOfStems(stack, staffSideEdge(nearest), halfWidth, stems, options.stemGap),
      options,
    );
    const offsets = ordered.map(
      (index) => Math.abs(staffSideEdge(marks[index]) - staffSideEdge(nearest)) * options.sizeRatio,
    );
    const extent = Math.max(...ordered.map((index, at) => offsets[at] + marks[index].drawnHeight));
    return { stack, ordered, edge, halfWidth, offsets, extent };
  });

  // The row is set by the stacks nearest the staff: every stack within the tolerance of
  // the nearest one joins it, at the height of the one furthest out among them.
  const rowFor = (side: FingerMarkSide): { nearest: number; edge: number } | undefined => {
    const edges = stacks.filter(({ stack }) => stack.side === side).map(({ edge }) => edge);
    if (edges.length === 0) return undefined;
    const nearest = nearestIn(side, edges);
    const inRow = edges.filter((edge) => Math.abs(edge - nearest) <= options.rowTolerance);
    return { nearest, edge: furthestOut(side, inRow) };
  };
  const rows: Record<FingerMarkSide, { nearest: number; edge: number } | undefined> = {
    above: rowFor('above'),
    below: rowFor('below'),
  };

  const placed = new Map<number, PlacedFingerMark>();
  stacks.forEach(({ stack, ordered, edge, halfWidth, offsets, extent }) => {
    const row = rows[stack.side];
    const rowEdge =
      row !== undefined && Math.abs(edge - row.nearest) <= options.rowTolerance ? row.edge : edge;
    const stackEdge = clearOfObstacles(
      stack,
      rowEdge,
      halfWidth,
      extent,
      obstacles,
      options.stemGap,
    );
    ordered.forEach((index, at) => {
      const mark = marks[index];
      const bottom =
        stack.side === 'above'
          ? stackEdge - offsets[at]
          : stackEdge + offsets[at] + mark.drawnHeight;
      placed.set(index, { left: mark.centerX - mark.drawnWidth / 2, bottom });
    });
  });
  return marks.map((_, index) => placed.get(index) as PlacedFingerMark);
};
